<script setup lang="ts">
import { DEFAULT_SECTION_SETTINGS, type TextSettings, type BackgroundSettings, type AudioSettings, type ThemeSettings } from '~/stores/auth'
import type { CharacterPageData, CharacterPageSettings, AudioPlaylistItem, ClipDisplayItem, FriendDisplayItem } from '~/composables/useCharacterPage'
import type { LinkItem } from '~/stores/links'
import type { CharacterAchievementItem } from '~/stores/achievements'
import type { GameItem } from '~/stores/games'
import type { SetupItem } from '~/stores/setups'

import { useLayoutStyles } from '~/composables/useLayoutStyles'
import { useCardHover } from '~/composables/useCardHover'

const authStore = useAuthStore()
const { requirePlan } = usePlanGate()
const richTextEnabled = computed(() => authStore.canAccessBoolean('richTextBio'))
const displayNameEnabled = computed(() => authStore.canAccessBoolean('displayName'))

const props = defineProps<{
  character: CharacterPageData
  settings: CharacterPageSettings
  textSettings: TextSettings
  backgroundSettings: BackgroundSettings
  audioSettings: AudioSettings
  themeSettings: ThemeSettings
  audioPlaylist: AudioPlaylistItem[]
  links: LinkItem[]
  achievements: CharacterAchievementItem[]
  games: GameItem[]
  clips: ClipDisplayItem[]
  setupItems: SetupItem[]
  friends: FriendDisplayItem[]
  isOwner: boolean
  editMode?: boolean
}>()

const emit = defineEmits<{
  'update:displayName': [value: string]
  'update:bio': [value: string]
  'update:location': [value: string]
  'update:pronouns': [value: string]
  'upload-avatar': []
  'open-avatar-section': []
  'open-links-manager': []
  'open-achievements-manager': []
  'open-games-manager': []
  'open-clips-section': []
  'upload-clip': []
  'open-setup-manager': []
  'open-youtube-settings': []
  'open-twitch-settings': []
  'open-spotify-settings': []
  'open-friends-manager': []
}>()

const {
  cardStyle,
  avatarBorder,
  avatarContainerStyle,
  avatarClipClass,
  avatarRingClass,
  avatarImageStyle,
  headingStyle,
  locationStyle,
  bioStyle,
  separatorStyle,
  orderedSections,
  getCardAnimationStyle,
  resolvedFamily
} = useLayoutStyles({
  themeSettings: toRef(props, 'themeSettings'),
  textSettings: toRef(props, 'textSettings'),
  settings: toRef(props, 'settings')
})

const { onCardMouseMove, onCardMouseLeave } = useCardHover(toRef(props, 'themeSettings'))

const sectionSettings = computed(() => props.settings.sections ?? DEFAULT_SECTION_SETTINGS)

// Provide layout context to child section components
provide('separatorStyle', separatorStyle)
provide('sectionInCard', ref(false))
provide('resolvedFontFamily', resolvedFamily)
</script>

<template>
  <div class="flex items-center justify-center min-h-dvh px-4 py-12">
    <div
      class="w-full max-w-lg mx-auto p-8 sm:p-12"
      :style="{ ...cardStyle, ...getCardAnimationStyle(0) }"
      @mousemove="onCardMouseMove"
      @mouseleave="onCardMouseLeave"
    >
      <!-- Avatar -->
      <div class="flex mb-6 justify-center">
        <div :style="avatarContainerStyle">
          <template v-if="editMode">
            <button
              v-if="settings.avatar_url"
              class="w-full h-full overflow-hidden cursor-pointer transition-all duration-200"
              :class="[avatarClipClass, avatarRingClass, avatarBorder === 'none' ? '' : 'hover:ring-primary-400/60']"
              @click="emit('open-avatar-section')"
            >
              <img
                :src="settings.avatar_url"
                :alt="settings.display_name || character.username"
                class="size-full object-cover"
                :style="avatarImageStyle"
              >
            </button>
            <div
              v-else
              class="w-full h-full flex items-center justify-center bg-white/2 border-2 border-dashed border-white/10 cursor-pointer group hover:bg-white/4 hover:border-white/15 transition-all duration-200"
              :class="avatarClipClass"
              @click="emit('upload-avatar')"
            >
              <UIcon
                name="i-lucide-image"
                class="size-6 text-white/20 group-hover:text-white/30 transition-colors"
              />
            </div>
          </template>

          <template v-else>
            <img
              v-if="settings.avatar_url"
              :src="settings.avatar_url"
              :alt="settings.display_name || character.username"
              class="w-full h-full object-cover"
              :class="[avatarClipClass, avatarRingClass]"
              :style="avatarImageStyle"
            >
          </template>
        </div>
      </div>

      <!-- Display Name + Badges (inline) -->
      <div class="flex items-center gap-2 flex-wrap mb-1 justify-center">
        <InlineEditText
          v-if="editMode && displayNameEnabled"
          :model-value="settings.display_name"
          :editable="true"
          tag="h1"
          :placeholder="character.username"
          :display-style="headingStyle"
          @update:model-value="emit('update:displayName', $event)"
        />
        <div
          v-else-if="editMode && !displayNameEnabled"
          class="inline-flex items-center gap-2 cursor-pointer rounded-lg transition-colors duration-200 hover:bg-white/5 -mx-3 px-3 -my-1 py-1"
          @click="requirePlan('Display Name', 'pro')"
        >
          <h1
            class="min-w-0 truncate"
            :style="headingStyle"
          >
            {{ character.username }}
          </h1>
          <UBadge
            color="fuchsia"
            variant="solid"
            size="xs"
            class="gap-0.5"
          >
            <UIcon
              name="i-lucide-lock"
              class="size-2"
            />
            Pro
          </UBadge>
        </div>
        <UTooltip
          v-else
          :text="character.uid ? `Character #${character.uid}` : 'Character'"
          :delay-duration="300"
        >
          <h1
            class="min-w-0 truncate"
            :style="headingStyle"
          >
            {{ settings.display_name || character.username }}
          </h1>
        </UTooltip>

        <AchievementDisplay
          :achievements="achievements"
          :text-settings="textSettings"
          :theme-settings="themeSettings"
          :edit-mode="editMode"
          @open-achievements-manager="emit('open-achievements-manager')"
        />
      </div>

      <!-- Pronouns -->
      <div
        v-if="settings.pronouns || editMode"
        class="flex justify-center mt-1"
      >
        <PronounsInput
          v-if="editMode"
          :model-value="settings.pronouns"
          @update:model-value="emit('update:pronouns', $event)"
        />
        <span
          v-else
          class="text-xs text-white/40"
        >{{ settings.pronouns }}</span>
      </div>

      <!-- Location -->
      <div
        v-if="settings.location || editMode"
        class="flex items-center gap-1.5 mt-3 mb-6 justify-center"
        :style="locationStyle"
      >
        <UIcon
          name="i-lucide-map-pin"
          class="size-3.5 shrink-0"
        />
        <InlineEditText
          v-if="editMode"
          :model-value="settings.location"
          :editable="true"
          tag="span"
          placeholder="Add location"
          :display-style="locationStyle"
          @update:model-value="emit('update:location', $event)"
        />
        <span v-else>{{ settings.location }}</span>
      </div>

      <!-- Separator -->
      <div
        v-if="(settings.bio || editMode) && separatorStyle"
        class="mb-6 mt-4 h-px mx-auto max-w-xs"
        :style="separatorStyle"
      />

      <!-- Bio -->
      <div
        v-if="editMode"
        class="max-w-md text-center mx-auto"
      >
        <TipTapEditor
          :model-value="settings.bio"
          :display-style="bioStyle"
          :rich-text-enabled="richTextEnabled"
          @update:model-value="emit('update:bio', $event)"
        />
      </div>
      <div
        v-else
        class="max-w-md text-center mx-auto"
      >
        <BioDisplay
          :bio="settings.bio"
          :bio-style="bioStyle"
        />
      </div>

      <!-- Dynamic sections (user-ordered) -->
      <template
        v-for="section in orderedSections"
        :key="section"
      >
        <SocialLinks
          v-if="section === 'links'"
          :links="links"
          :text-settings="textSettings"
          :theme-settings="themeSettings"
          :section-settings="sectionSettings"
          :edit-mode="editMode"
          :character-id="character.id"
          @open-links-manager="emit('open-links-manager')"
        />

        <GamesDisplay
          v-if="section === 'games'"
          :games="games"
          :text-settings="textSettings"
          :theme-settings="themeSettings"
          :section-settings="sectionSettings"
          :edit-mode="editMode"
          @open-games-manager="emit('open-games-manager')"
        />

        <ClipsDisplay
          v-if="section === 'clips'"
          :clips="clips"
          :theme-settings="themeSettings"
          :section-settings="sectionSettings"
          :text-settings="textSettings"
          :character-id="character.id"
          :edit-mode="editMode"
          @open-clips-section="emit('open-clips-section')"
          @upload-clip="emit('upload-clip')"
        />

        <FriendsDisplay
          v-if="section === 'friends'"
          :friends="friends"
          :text-settings="textSettings"
          :theme-settings="themeSettings"
          :section-settings="sectionSettings"
          :edit-mode="editMode"
          @open-friends-manager="emit('open-friends-manager')"
        />

        <SetupDisplay
          v-if="section === 'setup'"
          :setup-items="setupItems"
          :setup-photo-url="settings.setup_photo_url"
          :text-settings="textSettings"
          :theme-settings="themeSettings"
          :section-settings="sectionSettings"
          :character-id="character.id"
          :edit-mode="editMode"
          @open-setup-manager="emit('open-setup-manager')"
        />

        <DiscordEmbed
          v-if="section === 'discord' && character.discord_user_id"
          :character-id="character.id"
          :theme-settings="themeSettings"
          class="max-w-sm mx-auto mt-6"
        />

        <YouTubeEmbed
          v-if="section === 'youtube' && character.youtube_channel_id"
          :character-id="character.id"
          :theme-settings="themeSettings"
          :youtube-settings="settings.youtube"
          :edit-mode="editMode"
          class="max-w-sm mx-auto mt-6"
          @open-youtube-settings="emit('open-youtube-settings')"
        />

        <TwitchEmbed
          v-if="section === 'twitch' && character.twitch_user_id"
          :character-id="character.id"
          :theme-settings="themeSettings"
          :twitch-settings="settings.twitch"
          :edit-mode="editMode"
          class="max-w-sm mx-auto mt-6"
          @open-twitch-settings="emit('open-twitch-settings')"
        />

        <SpotifyEmbed
          v-if="section === 'spotify' && character.spotify_user_id"
          :character-id="character.id"
          :theme-settings="themeSettings"
          :spotify-settings="settings.spotify"
          :edit-mode="editMode"
          class="max-w-sm mx-auto mt-6"
          @open-spotify-settings="emit('open-spotify-settings')"
        />
      </template>
    </div>
  </div>
</template>
